// @/components/reusable/treemap-chart.jsx

/**
 * TreemapChart - Generic hierarchical treemap visualization
 *
 * A flexible treemap component for displaying proportional data with:
 * - Squarified layout algorithm for optimal rectangles
 * - Hierarchical data support (nested children)
 * - Smooth animations via Framer Motion (staggered slide-up with animated numbers)
 * - Click interactions with DataPopover integration
 * - Responsive sizing with ResizeObserver
 * - Color gradients based on value or index
 * - Smart label truncation to prevent overlap
 *
 * Architecture:
 * - Pure Tailwind CSS for styling (no external charting library)
 * - Custom squarify implementation for layout
 * - Motion animations for entrance and hover effects
 * - DataPopover for click-triggered details
 *
 * Usage:
 * ```jsx
 * <TreemapChart
 *   data={[
 *     { key: 'scope1', name: 'Scope 1', value: 4091, percentage: 22.3 },
 *     { key: 'scope2', name: 'Scope 2', value: 1800, percentage: 9.9, children: [...] },
 *   ]}
 *   onCellClick={(item, position) => handleClick(item, position)}
 * />
 * ```
 */

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'motion/react';
import AnimatedNumber from '@/components/reusable/animated-number';

// ============================================================================
// CONSTANTS
// ============================================================================

const MIN_CELL_WIDTH = 60;
const MIN_CELL_HEIGHT = 40;
const CELL_GAP = 4; // Gap between cells in pixels
const CELL_RADIUS = 24; // Border radius matching rounded-2xl

// Animation timing - snappy spring matching other charts
const cellSpring = {
  type: 'spring',
  stiffness: 200,
  damping: 20,
  mass: 0.5,
};

// Stagger delay between cells
const STAGGER_DELAY = 0.05;

// Color palette - primary blue gradient with grays for smaller items
const COLOR_PALETTE = {
  // Primary series (high values)
  primary: [
    'bg-primary',           // #3B82F6 - Main primary
    'bg-primary/90',
    'bg-primary/80',
    'bg-primary/70',
  ],
  // Secondary series (medium values)
  secondary: [
    'bg-slate-600',
    'bg-slate-500',
    'bg-slate-400',
  ],
  // Tertiary series (small values)
  tertiary: [
    'bg-indigo-100',
    'bg-slate-200',
    'bg-slate-100',
  ],
};

/**
 * Truncate label to fit within available width
 * Uses character count estimation based on cell width
 *
 * @param {string} name - Full label name
 * @param {number} cellWidth - Available cell width in pixels
 * @returns {string} - Truncated label if needed
 */
const truncateLabel = (name, cellWidth) => {
  if (!name) return '';

  // Estimate characters that fit (assume ~8px per char for text-sm font)
  // Account for padding (16px each side = 32px total)
  const availableWidth = cellWidth - 32;
  const charsPerWidth = availableWidth / 8;

  // Minimum 3 characters, then truncate
  const maxChars = Math.max(3, Math.floor(charsPerWidth));

  if (name.length <= maxChars) {
    return name;
  }

  // Truncate and return (no ellipsis to keep it clean)
  return name.substring(0, maxChars);
};

// ============================================================================
// SQUARIFY ALGORITHM
// ============================================================================

/**
 * Calculate worst aspect ratio for a row of items
 */
const worstRatio = (row, rowSum, shortSide) => {
  if (row.length === 0 || shortSide === 0) return Infinity;
  const rowLength = rowSum / shortSide;
  if (rowLength === 0) return Infinity;

  return Math.max(
    ...row.map((item) => {
      const itemLength = item.area / rowLength;
      if (itemLength === 0) return Infinity;
      return Math.max(rowLength / itemLength, itemLength / rowLength);
    })
  );
};

/**
 * Squarify algorithm - creates optimal rectangular layout for treemap
 *
 * @param {Array} data - Array of items with { value, ... }
 * @param {number} x - Starting x position
 * @param {number} y - Starting y position
 * @param {number} width - Available width
 * @param {number} height - Available height
 * @returns {Array} Array of items with x, y, width, height added
 */
const squarify = (data, x, y, width, height) => {
  if (!data || data.length === 0 || width <= 0 || height <= 0) return [];

  const total = data.reduce((sum, d) => sum + (d.value || 0), 0);
  if (total === 0) return [];

  const totalArea = width * height;
  const result = [];
  let remaining = [...data].map((d) => ({
    ...d,
    area: (d.value / total) * totalArea,
  }));

  let currentX = x;
  let currentY = y;
  let currentWidth = width;
  let currentHeight = height;

  while (remaining.length > 0) {
    const isHorizontal = currentWidth >= currentHeight;
    const shortSide = isHorizontal ? currentHeight : currentWidth;

    let row = [];
    let rowSum = 0;

    for (const item of remaining) {
      const testRow = [...row, item];
      const testRowSum = rowSum + item.area;
      const testWorst = worstRatio(testRow, testRowSum, shortSide);
      const currentWorst = row.length > 0 ? worstRatio(row, rowSum, shortSide) : Infinity;

      if (row.length === 0 || testWorst <= currentWorst) {
        row.push(item);
        rowSum = testRowSum;
      } else {
        break;
      }
    }

    // Layout the row
    const rowLength = shortSide > 0 ? rowSum / shortSide : 0;
    let offset = 0;

    for (const item of row) {
      const itemLength = rowLength > 0 ? item.area / rowLength : 0;

      if (isHorizontal) {
        result.push({
          ...item,
          x: currentX,
          y: currentY + offset,
          width: rowLength,
          height: itemLength,
        });
      } else {
        result.push({
          ...item,
          x: currentX + offset,
          y: currentY,
          width: itemLength,
          height: rowLength,
        });
      }
      offset += itemLength;
    }

    // Update remaining space
    if (isHorizontal) {
      currentX += rowLength;
      currentWidth -= rowLength;
    } else {
      currentY += rowLength;
      currentHeight -= rowLength;
    }

    remaining = remaining.slice(row.length);
  }

  return result;
};

// ============================================================================
// CELL COMPONENT
// ============================================================================

/**
 * TreemapCell - Individual cell in the treemap
 *
 * Features:
 * - Staggered slide-up fade animation (like cards)
 * - Animated count-up percentage number
 * - Smart label truncation to prevent overflow
 * - h4-style consistent typography
 */
const TreemapCell = ({
  item,
  index,
  totalItems: _totalItems,
  totalValue,
  isHovered,
  onHover,
  onLeave,
  onClick,
  formatLabel,
  formatValue,
}) => {
  const containerRef = useRef(null);

  // Determine cell color based on index and value (or custom colorClass from item)
  const getColorClass = useCallback(() => {
    // If item has a custom colorClass, use it
    if (item.colorClass) return item.colorClass;

    // First item (highest value) gets primary
    if (index === 0) return COLOR_PALETTE.primary[0];

    // Calculate percentage of total
    const percentage = (item.value / totalValue) * 100;

    // Large items (>10%) get primary shades
    if (percentage > 10) {
      const shade = Math.min(index, COLOR_PALETTE.primary.length - 1);
      return COLOR_PALETTE.primary[shade];
    }

    // Medium items (3-10%) get secondary colors
    if (percentage > 3) {
      const shade = Math.min(index - 3, COLOR_PALETTE.secondary.length - 1);
      return COLOR_PALETTE.secondary[Math.max(0, shade)];
    }

    // Small items get tertiary colors
    const shade = Math.min(index - 6, COLOR_PALETTE.tertiary.length - 1);
    return COLOR_PALETTE.tertiary[Math.max(0, shade)];
  }, [index, item.value, totalValue]);

  // Determine text color based on background
  const getTextColorClass = useCallback(() => {
    const colorClass = getColorClass();
    // Light backgrounds need dark text
    if (colorClass.includes('100') || colorClass.includes('200')) {
      return 'text-zinc-700';
    }
    return 'text-white';
  }, [getColorClass]);

  // Get secondary text color (for percentage)
  const getSecondaryTextColorClass = useCallback(() => {
    const colorClass = getColorClass();
    // Light backgrounds need dark text
    if (colorClass.includes('100') || colorClass.includes('200')) {
      return 'text-zinc-500';
    }
    return 'text-white/80';
  }, [getColorClass]);

  // Calculate cell dimensions (accounting for gap)
  const cellWidth = Math.max(0, item.width - CELL_GAP);
  const cellHeight = Math.max(0, item.height - CELL_GAP);

  // Calculate if we should show labels based on cell size
  const showFullLabel = cellWidth > MIN_CELL_WIDTH * 1.5 && cellHeight > MIN_CELL_HEIGHT * 1.2;
  const showPercentage = cellWidth > MIN_CELL_WIDTH && cellHeight > MIN_CELL_HEIGHT;
  const showAnyLabel = cellWidth > MIN_CELL_WIDTH * 0.8 && cellHeight > MIN_CELL_HEIGHT * 0.8;

  // Calculate percentage for display
  const percentageValue = totalValue > 0 ? (item.value / totalValue) * 100 : 0;

  // Truncate label to fit available width
  const displayLabel = formatLabel ? formatLabel(item, cellWidth) : truncateLabel(item.name, cellWidth);

  // Stagger delay for this cell's animation
  const baseDelay = index * STAGGER_DELAY;

  // Handle click with position
  const handleClick = (event) => {
    event.stopPropagation();
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const position = {
        x: rect.left + rect.width / 2,
        y: rect.top,
      };
      onClick?.(item, position);
    }
  };

  const colorClass = getColorClass();
  const textColorClass = getTextColorClass();
  const secondaryTextColorClass = getSecondaryTextColorClass();

  // Optional custom value display
  const valueDisplay = formatValue ? formatValue(item) : null;

  return (
    <motion.button
      ref={containerRef}
      type="button"
      initial={{ opacity: 0, y: 16, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ ...cellSpring, delay: baseDelay }}
      className={clsx(
        'absolute flex flex-col items-center justify-center text-center cursor-pointer overflow-hidden',
        colorClass,
        isHovered && 'ring-2 ring-white/50 z-10 brightness-110'
      )}
      style={{
        left: item.x + CELL_GAP / 2,
        top: item.y + CELL_GAP / 2,
        width: cellWidth,
        height: cellHeight,
        borderRadius: CELL_RADIUS,
      }}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onClick={handleClick}
      aria-label={`${item.name}: ${percentageValue.toFixed(1)}%`}
    >
      {showAnyLabel && (
        <div className="p-2 flex flex-col items-center justify-center gap-0.5">
          {/* Label - h4 style: text-base font-semibold */}
          {showFullLabel && (
            <motion.span
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: baseDelay + 0.15 }}
              className={clsx(
                'font-semibold text-base leading-tight tracking-tight',
                textColorClass
              )}
            >
              {displayLabel}
            </motion.span>
          )}
          {/* Custom value display or percentage - animated count-up with monospace font */}
          {showPercentage && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: baseDelay + 0.25 }}
              className={clsx(
                'font-fono text-sm tabular-nums',
                secondaryTextColorClass
              )}
            >
              {valueDisplay || (
                <>
                  <AnimatedNumber
                    value={percentageValue}
                    delay={baseDelay + 0.2}
                    duration={0.6}
                    format={(v) => v.toFixed(1)}
                  />
                  %
                </>
              )}
            </motion.span>
          )}
        </div>
      )}
    </motion.button>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * TreemapChart - Main treemap visualization component
 *
 * @param {Object} props
 * @param {Array} props.data - Array of data items with { key, name, value, children? }
 * @param {number} props.height - Chart height in pixels (default: 400)
 * @param {Function} props.onCellClick - Callback when cell is clicked (item, position)
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.dataKey - Optional key to force remount when data changes
 * @param {Function} props.formatLabel - Optional custom label formatter (item, cellWidth) => string
 * @param {Function} props.formatValue - Optional custom value formatter (item) => string|ReactNode
 */
const TreemapChart = ({
  data,
  height, // Optional fixed height - if not provided, uses flex to fill container
  onCellClick,
  className,
  dataKey,
  formatLabel,
  formatValue,
}) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const [hoveredKey, setHoveredKey] = useState(null);

  // Track container dimensions with ResizeObserver
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
        setContainerHeight(entry.contentRect.height);
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Flatten hierarchical data for layout calculation
  const flattenedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // If data has children, flatten them into a single level
    const flattened = [];

    data.forEach((item) => {
      if (item.children && item.children.length > 0) {
        // Add children with parent reference
        item.children.forEach((child) => {
          flattened.push({
            ...child,
            parentKey: item.key,
            parentName: item.name,
          });
        });
      } else {
        flattened.push(item);
      }
    });

    // Sort by value descending
    return flattened.sort((a, b) => (b.value || 0) - (a.value || 0));
  }, [data]);

  // Calculate total value
  const totalValue = useMemo(() => {
    return flattenedData.reduce((sum, item) => sum + (item.value || 0), 0);
  }, [flattenedData]);

  // Determine effective height - use prop if provided, otherwise use container height
  const effectiveHeight = height || containerHeight;

  // Calculate layout using squarify algorithm
  const layout = useMemo(() => {
    if (containerWidth === 0 || effectiveHeight === 0 || flattenedData.length === 0) return [];
    return squarify(flattenedData, 0, 0, containerWidth, effectiveHeight);
  }, [flattenedData, containerWidth, effectiveHeight]);

  // Handle cell click
  const handleCellClick = useCallback(
    (item, position) => {
      onCellClick?.(item, position);
    },
    [onCellClick]
  );

  // Empty state
  if (!data || data.length === 0 || totalValue === 0) {
    return (
      <div
        className={clsx('flex items-center justify-center text-zinc-400', height ? '' : 'h-full flex-1', className)}
        style={height ? { height } : undefined}
      >
        No data available
      </div>
    );
  }

  // Generate a stable key from data for forcing remount on data changes
  const contentKey = dataKey || data.map((d) => `${d.key}-${d.value}`).join('|');

  return (
    <div
      ref={containerRef}
      className={clsx('relative', height ? '' : 'h-full flex-1', className)}
      style={height ? { height } : undefined}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={contentKey}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0"
        >
          {layout.map((item, index) => (
            <TreemapCell
              key={item.key || item.name || index}
              item={item}
              index={index}
              totalItems={layout.length}
              totalValue={totalValue}
              isHovered={hoveredKey === (item.key || item.name)}
              onHover={() => setHoveredKey(item.key || item.name)}
              onLeave={() => setHoveredKey(null)}
              onClick={handleCellClick}
              formatLabel={formatLabel}
              formatValue={formatValue}
            />
          ))}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default TreemapChart;
